import { z } from 'zod';
import { countryCodeSchema, currencyCodeSchema, emailSchema, handleSchema } from './primitives.js';
/**
 * Auth contracts shared by apps/api and the dashboards.
 *
 * TOKEN MODEL
 *   access token  — short-lived JWT, returned in the response BODY and held in
 *                   memory by the SPA. Never persisted, so an XSS payload
 *                   cannot lift it from localStorage after the tab closes.
 *   refresh token — long-lived opaque random string, delivered ONLY as an
 *                   httpOnly cookie. JavaScript cannot read it at all.
 *
 * The split is why the login response contains an access token but no refresh
 * token: the client never needs to see the latter.
 */
export const loginRequestSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Password is required'),
});
export const TENANT_ROLES = ['OWNER', 'ADMIN', 'STAFF', 'VIEWER'];
/**
 * Self-serve store creation.
 *
 * One request creates the person AND their first store: an account with no
 * store cannot do anything, so splitting them would leave a user stranded on
 * an empty dashboard.
 */
export const signupRequestSchema = z.object({
    email: emailSchema,
    password: z.string().min(12, 'Use at least 12 characters').max(200, 'That password is too long'),
    firstName: z.string().max(255).optional(),
    lastName: z.string().max(255).optional(),
    storeName: z.string().min(1, 'Store name is required').max(255),
    /**
     * The subdomain the storefront is served from. Optional — derived from the
     * store name when omitted, which is what the signup form does.
     */
    storeSlug: handleSchema.optional(),
    currencyCode: currencyCodeSchema.default('USD'),
    countryCode: countryCodeSchema.default('US'),
    /**
     * The language they signed up in, saved on the user so the emails we send
     * them match from the very first one. Optional: signup forms that do not
     * offer a language picker simply omit it.
     */
    locale: z.string().min(2).max(8).optional(),
});
export const updateStoreSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    slug: handleSchema.optional(),
    /** Null clears it and falls back to the subdomain. */
    customDomain: z
        .string()
        .max(253)
        .regex(/^[a-z0-9.-]+$/i, 'Enter a bare hostname, without https:// or a path')
        .nullable()
        .optional(),
    currencyCode: currencyCodeSchema.optional(),
    countryCode: countryCodeSchema.optional(),
    timezone: z.string().max(64).optional(),
    /**
     * Locale codes are validated against @upweo/i18n in the service, not here.
     * This package must stay dependency-light enough to bundle into a browser,
     * and duplicating the list would let the two drift apart.
     */
    defaultLocale: z.string().min(2).max(8).optional(),
    enabledLocales: z.array(z.string().min(2).max(8)).min(1).max(32).optional(),
});
/**
 * Password reset.
 *
 * The request form takes only an email, and the response never varies — see
 * the service for why revealing whether an account exists is the thing this
 * flow must not do.
 */
export const requestPasswordResetSchema = z.object({
    email: z.string().trim().toLowerCase().email('Enter a valid email address'),
});
export const completePasswordResetSchema = z.object({
    token: z.string().min(1, 'This reset link is missing its token'),
    password: z.string().min(12, 'Use at least 12 characters').max(200, 'That password is too long'),
});
/**
 * The signed-in user's own preferences.
 *
 * Locale is persisted server-side, not just in localStorage, because it has to
 * survive into contexts with no browser at all — a password-reset email is
 * composed by the API from a request the user is not present for.
 */
export const updateMeSchema = z.object({
    locale: z.string().min(2).max(8).optional(),
});
//# sourceMappingURL=auth.js.map